import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { UnitToggle } from '@/components/UnitToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  resolveUnits, weightUnit, displayWeight, weightToKg, weightPlaceholder,
} from '@/lib/units';
import { Settings2, Save, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = ['beginner', 'intermediate', 'advanced'];

export const PreferencesCard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [units, setUnits] = useState(resolveUnits(user));
  const [weightKg, setWeightKg] = useState(user?.weight_kg ?? null);
  const [level, setLevel] = useState(user?.skill_level || 'beginner');
  const [saving, setSaving] = useState(false);

  const weightDisplay = displayWeight(weightKg, units);

  const onWeightInput = (e) => setWeightKg(weightToKg(e.target.value, units));

  const onUnitChange = (u) => {
    setUnits(u);
    if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', u);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/preferences', { units, weight_kg: weightKg, skill_level: level });
      if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', units);
      await refreshUser();
      toast.success(t('preferences.saved'));
      setOpen(false);
    } catch (e) {
      toast.error(t('preferences.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kite-card p-5" data-testid="preferences-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center"><Settings2 className="w-5 h-5 text-[#00B4D8]" /></div>
          <div>
            <h3 className="font-semibold">{t('preferences.title')}</h3>
            <p className="text-xs text-muted-foreground">
              {units === 'imperial' ? t('preferences.imperialUnits') : t('preferences.metricUnits')}
              {weightDisplay != null ? ` · ${weightDisplay} ${weightUnit(units)}` : ''}
              {` · ${t(`levels.${level}`, { defaultValue: level })}`}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setOpen((o) => !o)} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="preferences-toggle">
          {open ? <>{t('preferences.close')} <ChevronUp className="w-4 h-4 ml-1" /></> : <>{t('preferences.edit')} <ChevronDown className="w-4 h-4 ml-1" /></>}
        </Button>
      </div>

      {open && (
        <div className="mt-5 space-y-5">
          <div>
            <Label className="text-xs text-muted-foreground">{t('preferences.unitSystem')}</Label>
            <div className="mt-1.5"><UnitToggle value={units} onChange={onUnitChange} testid="preferences-unit-toggle" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pref-weight" className="text-xs text-muted-foreground">{t('preferences.weight', { unit: weightUnit(units) })}</Label>
              <Input
                id="pref-weight"
                type="number"
                inputMode="decimal"
                value={weightDisplay ?? ''}
                onChange={onWeightInput}
                placeholder={weightPlaceholder(units)}
                className="mt-1.5 bg-[#0A0A0A] border-white/15"
                data-testid="preferences-weight"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('preferences.skillLevel')}</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="preferences-level"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12161B] border-white/15 text-white">
                  {LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`levels.${l}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="preferences-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('preferences.savePreferences')}</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
